import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Shield, Heart, Brain, Users, Target, 
  ArrowRight, ArrowLeft, Clock, Lock, Award,
  Stethoscope, FileCheck, UserCheck, ChevronDown, ChevronUp
} from 'lucide-react';
import '../styles/WhyWeExist.css';
import '../styles/Demo.css';
import Footer from '../components/Footer';
import UniversalCTA from '../components/UniversalCTA';
import LuxeCompassWatermark from '../components/LuxeCompassWatermark';

// Sparkly EKG Tap Navigator - user-provided transparent image
const SparklyEKG = ({ onClick, className = "" }) => (
  <button 
    className={`sparkly-ekg-btn ${className}`}
    onClick={onClick}
    aria-label="Tap to see next"
  >
    <img 
      src="/sparkly-ekg-gold.png" 
      alt="Tap for next" 
      className="sparkly-ekg-img"
    />
  </button>
);

// Disparities carousel items - WHO WE EXIST FOR
const disparitiesItems = [
  "The elderly patient waved away as 'just aging'",
  "The veteran told it's 'just stress'",
  "The woman whose symptoms were dismissed as 'hormonal'",
  "Anyone who's waited years to get a diagnosis that could have changed everything"
];

const Services = () => {
  // Auto-expanded by default 2026-02-08 (founder spec "I want the
  // Who We Serve box auto-expanded" + "When an Expert Opinion is
  // Needed kept auto-expanded"). Switched from single-string state to
  // a Set so multiple sections can stay open at once.
  const [expandedSections, setExpandedSections] = useState(new Set(['serve', 'essential']));
  const [flippedCards, setFlippedCards] = useState([]);

  const toggleSection = (section) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  };

  // Toggle flip card - cards STAY flipped (don't auto-close others)
  const toggleFlipCard = (cardId) => {
    setFlippedCards(prev => 
      prev.includes(cardId) 
        ? prev.filter(id => id !== cardId)  // Unflip if already flipped
        : [...prev, cardId]  // Add to flipped list (keep others open)
    );
  };

  return (
    <div className="services-page">
      {/* STANDARDIZED: Emblem section - same as other pages */}
      <section className="emblem-section" data-testid="services-emblem">
        <div className="container">
          <div className="emblem-wrapper">
            <img src="/cadence-gold-foil.png" alt="CADENCE Medical Consulting" className="velvet-emblem-home" />
          </div>
        </div>
      </section>

      {/* UNIFIED Title and Quote - consistent with other pages */}
      <section className="unified-content-section" data-testid="services-title">
        <div className="container">
          <LuxeCompassWatermark />
          <h1 className="unified-page-title">Healthcare Disparities<br />Are Personal to Us</h1>
        </div>
      </section>
      
      <section className="unified-quote-section">
        <div className="container">
          {/* Hero quote — trimmed 2026-02 (founder follow-up: "the
              quote on that page at the very top is too long but I
              still want the same potency"). Was 28 words across two
              sentences; now 16 — keeps the contrast structure
              (concerns dismissed → attention earned) and the
              long-deserved warrant language. */}
          <p className="unified-page-quote">
            &ldquo;Too often, concerns are dismissed. At CADENCE, your case earns the attention it has long deserved.&rdquo;
          </p>
          <div className="hero-buttons">
            <Link 
              to="/contact"
              className="btn-primary"
              data-testid="schedule-consultation-why"
            >
              Request Consultation
            </Link>
            <Link to="/pricing" className="btn-secondary">
              View Pricing
            </Link>
          </div>
        </div>
      </section>

      {/* DISPARITIES carousel box DELETED 2026-02-08 (founder spec
          "delete the first box"). The carousel content + its
          rotating quotes are preserved in /app/memory/
          SAVED_DISPARITIES_CAROUSEL.md for future paste-back. */}

      {/* Hospital bed family photo MOVED 2026-02-08 — now shares the
          "When an Expert Opinion Is Needed" box (founder spec "share
          the image of the bed with that box"). */}

      {/* Who We Serve — sits IMMEDIATELY under the mission statement
          (2026-02 second pass: was previously rendered after the hospital
          photo break, which pushed it too far from the question). The
          tile + its inner serve cards share the same copper conic
          shimmer border so the section reads as one luxe unit. */}
      <section className="who-we-serve">
        <div className="container">
          <div className="expandable-card serve-outer-tile">
            <div 
              className="expandable-header"
              onClick={() => toggleSection('serve')}
            >
              <div className="header-content">
                <div>
                  <h2 className="heading-2">Who We Serve</h2>
                  <p className="body-medium">Women, veterans, and anyone navigating complex diagnoses who deserves to be heard.</p>
                </div>
              </div>
              <button className="expand-toggle">
                {expandedSections.has('serve') ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
              </button>
            </div>
            
            {expandedSections.has('serve') && (
              <div className="expandable-content">
                <div className="serve-grid">
                  <div className="serve-card">
                    <h3 className="heading-3">Women&rsquo;s Health</h3>
                    <p className="body-small">PCOS, endometriosis, thyroid disorders, abnormal bleeding, fertility concerns</p>
                  </div>
                  <div className="serve-card">
                    <h3 className="heading-3">Veterans</h3>
                    <p className="body-small">Navigating VA care, service-connected conditions, gender-specific services</p>
                  </div>
                  <div className="serve-card">
                    <h3 className="heading-3">Complex Cases</h3>
                    <p className="body-small">Autoimmune conditions, chronic illness, undiagnosed symptoms</p>
                  </div>
                  <div className="serve-card">
                    <h3 className="heading-3">The Dismissed</h3>
                    <p className="body-small">Anyone told &ldquo;it&rsquo;s just stress&rdquo; or left without answers</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Hospital bed photo moved INTO the Expert Opinion box
          (2026-02-08, founder spec). This section deleted. */}

      {/* When an Expert Opinion Is Needed — GOLD SOLUTION
          (renamed 2026-02-08 from "Second Opinion" per founder spec;
          stat updated to 1 in 8 Americans; box auto-expanded by
          default; hospital-bed photo + doctor-couple photo now BOTH
          live inside the box with a single squished paragraph
          between them). */}
      <section className="essential-section">
        <div className="container">
          <div className="expandable-card gold-solution-card">
            <div 
              className="expandable-header"
              onClick={() => toggleSection('essential')}
            >
              <div className="header-content">
                <div>
                  <h2 className="heading-2">When Is an Expert Opinion Needed?</h2>
                  <p className="body-medium">1 in 8 Americans is misdiagnosed each year. An expert opinion can confirm diagnoses or reveal new conditions.</p>
                </div>
              </div>
              <button className="expand-toggle">
                {expandedSections.has('essential') ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
              </button>
            </div>

            {expandedSections.has('essential') && (
              <div className="expandable-content expert-opinion-content">
                {/* Two-image sandwich (founder spec 2026-02-08
                    "share the bed image in the box; squish first
                    paragraph between the 2 images; delete the
                    other 2 paragraphs"). */}
                <div className="expert-opinion-stack">
                  <div className="expert-opinion-photo">
                    <img
                      src="/local-images/why-we-exist-photo.jpg"
                      alt="Family gathered around loved one in hospital bed"
                    />
                  </div>
                  <p className="expert-opinion-blurb">
                    A serious diagnosis is overwhelming, raising critical questions about accuracy and treatment. At CADENCE, we leverage decades of military medical expertise for comprehensive analysis and clarity.
                  </p>
                  <div className="expert-opinion-photo">
                    <img
                      src="/consultation-empathy.png"
                      alt="Expert consultation"
                    />
                  </div>
                </div>
                <p className="source-citation">Source: Singh H, et al. <em>Diagnosis (Berlin).</em> 2025.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Compassionate-care doctor-patient photo — the photo stays
          flat / free-floating with NO brown tile behind it (founder
          spec). The Health Equity plaque sits ABOVE the photo as a
          heritage byline (founder follow-up 2026-02: "the advocate
          & patient champion placard needs to be above its photo not
          below"). */}
      <section className="compassionate-care-section">
        <div className="container">
          <div className="story-fivew-plaque-wrap health-equity-plaque-wrap">
            <div className="story-fivew-plaque health-equity-plaque" data-testid="health-equity-plaque">
              <div className="story-fivew-plaque-inner">
                <span className="story-fivew-eyebrow health-equity-eyebrow">CADENCE MEDICAL CONSULTING</span>
                <h3 className="story-fivew-title health-equity-title">
                  Your Health Equity Experts &amp; Patient Champions
                </h3>
              </div>
            </div>
          </div>
          <div className="care-image-container feathered-photo">
            <img src="/doctor-patient.png" alt="Compassionate healthcare consultation" className="care-image feathered-img" />
          </div>
        </div>
      </section>

      {/* How It Works — moved to CADENCE Promise page (after pie/puzzle)
          per 2026-05-19 user request. */}

      {/* Who We Serve relocated above (2026-02) so it sits beside
          "This is the pain we exist to fix" — see disparities section. */}

      {/* Photo Break */}
      <section className="photo-break">
        <div className="container">
          <div className="photo-content">
            <img src="/family-trust.png" alt="Family receiving care" />
            <div className="photo-overlay">
              <h2 className="heading-2">Your Health. Your Advocate. Your Future.</h2>
              <p className="body-large">Join hundreds of patients who finally got the answers they deserved.</p>
              <Link to="/contact" className="btn-primary">Start Your Journey</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Back nav — symmetry with /our-story + /demo (founder feedback
          2026-02 "I'd like back to founders + back to home pills at
          the bottom of this page too"). */}
      <section className="demo-back-section" data-testid="why-back-section">
        <Link to="/founders" className="demo-back-link" data-testid="why-back-founders">
          <ArrowLeft size={16} />
          Back to Founders
        </Link>
        <Link to="/home" className="demo-back-link" data-testid="why-back-home">
          <ArrowLeft size={16} />
          Back to Home
        </Link>
      </section>

      {/* Footer */}
      <UniversalCTA />
      <Footer />
    </div>
  );
};

export default Services;
